import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Eye, EyeOff, Lock, Mail, AlertCircle } from "lucide-react";

function formatError(detail) {
  if (!detail) return "Error al iniciar sesión.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await login(email, password);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0072A4] to-[#0F3C6B] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
            <Lock size={32} color="white" />
          </div>
          <h1 className="font-['Montserrat'] font-bold text-3xl text-white mb-2">Panel Admin</h1>
          <p className="text-white/70 font-['Montserrat'] text-sm">Campus Verano Activo La Estanca 2026</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl p-8 shadow-2xl"
          data-testid="login-form"
        >
          <div className="space-y-4">
            <div>
              <label className="block font-['Montserrat'] font-semibold text-[#0F172A] text-sm mb-1">Email</label>
              <div className="relative">
                <Mail size={16} color="#94A3B8" className="absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  placeholder="admin@campusverano.com"
                  data-testid="login-email"
                  className="w-full border-2 border-[#E2E8F0] rounded-xl pl-10 pr-4 py-3 text-[#0F172A] font-['Montserrat'] text-sm focus:outline-none focus:border-[#0072A4] transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block font-['Montserrat'] font-semibold text-[#0F172A] text-sm mb-1">Contraseña</label>
              <div className="relative">
                <Lock size={16} color="#94A3B8" className="absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  data-testid="login-password"
                  className="w-full border-2 border-[#E2E8F0] rounded-xl pl-10 pr-12 py-3 text-[#0F172A] font-['Montserrat'] text-sm focus:outline-none focus:border-[#0072A4] transition-colors"
                />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2">
                  {showPwd ? <EyeOff size={16} color="#94A3B8" /> : <Eye size={16} color="#94A3B8" />}
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3 mt-4" data-testid="login-error">
              <AlertCircle size={16} color="#EF4444" />
              <p className="text-red-600 font-['Montserrat'] text-sm">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full mt-6 bg-[#0072A4] text-white font-['Montserrat'] font-bold py-3 rounded-xl hover:bg-[#005A80] active:scale-95 transition-all disabled:opacity-60"
          >
            {loading ? "Accediendo..." : "Iniciar Sesión"}
          </button>

          <div className="mt-4 text-center">
            <a href="/" className="text-[#0072A4] font-['Montserrat'] text-sm hover:underline" data-testid="back-to-site">
              ← Volver al sitio web
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
